import {
  AppConfiguration,
  useAppConfiguration,
} from "@/api/app-configuration.api";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { differenceInDays, startOfDay } from "date-fns";
import { atom } from "jotai";
import { ComponentProps, useMemo } from "react";
import z from "zod";

export const trialCTAModalPropsAtom =
  atom<ComponentProps<typeof TrialCTAModal>>();

export function getTrialPeriod(appConfiguration: AppConfiguration) {
  const {
    subscription: { trial, expired, from, to },
  } = appConfiguration;

  // This implements the ability to test trials in production without making a proper trial. The value should be an ISO8601 date string and will be used for the endDate of a trial
  const search = new URLSearchParams(location.search);
  const validate = z.iso.date().optional();
  const trialTest = validate.safeParse(search.get("internal_trial_test"));

  if (
    (import.meta.env.DEV &&
      import.meta.env.VITE_MOCK_TRIAL_PERIOD === "true") ||
    (trialTest.success && trialTest.data)
  ) {
    const fromDate = new Date("2026-01-01");
    const toDate = trialTest.data
      ? new Date(trialTest.data)
      : new Date("9999-12-31"); // Set it to any date, you can also use "9999-12-31" to represent a practically infinite trial period

    return {
      isExpired: toDate.getTime() <= new Date().getTime(),
      fromDate,
      toDate,
      daysTotal: differenceInDays(toDate, fromDate),
      daysRemaining: differenceInDays(toDate, new Date()),
    };
  }

  if (!trial || !from || !to) return null;

  const fromDate = new Date(from);
  const toDate = new Date(to);

  const daysTotal = differenceInDays(toDate, fromDate);
  const daysRemaining = differenceInDays(toDate, startOfDay(new Date()));

  return {
    isExpired: expired,
    fromDate,
    toDate,
    daysTotal,
    daysRemaining,
  };
}

export function useTrialPeriod() {
  const appConfiguration = useAppConfiguration().data;
  if (!appConfiguration) throw Error("App configuration was not found");
  return useMemo(() => getTrialPeriod(appConfiguration), [appConfiguration]);
}

export const trialText =
  "Ikke tilgængeligt i prøveperioden. Kontakt os for at få adgang til Lasso Sales uden begrænsninger.";
export const trialExpiredText =
  "Din prøveperiode er udløbet. Kontakt os for at få adgang til Lasso Sales uden begrænsninger.";
export const essentialsText =
  "Opgrader til Pro for at få adgang til avanceret segmentering og salgstriggers.";
export const essentialsCreditsText =
  "Opgrader til Pro for at tilkøbe flere credits.";

export type TrialPeriod = ReturnType<typeof getTrialPeriod>;
